import { ColDef } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ClientSideAgGrid from '../../components/common/customAgGrid/ClientSideAgGrid';
import CustomAgDateFilter from '../../components/common/customAgGrid/CustomAgDateFilter';
import CustomAgMultiSelectFilter from '../../components/common/customAgGrid/CustomAgMultiSelectFilter';
import CustomAgNumberFilter from '../../components/common/customAgGrid/CustomAgNumberFilter';
import Stack from '@mui/material/Stack';
import PageHeader from '../../components/PageHeader';
import { useGetLubricationChartsListDataQuery } from '../../store/apiSlices/lubricationChart/lubricationChartsApis';
import { canEditLubricationCharts } from '../../utils/permissions';
import { isAdmin, isEditor } from '../../utils/roles';
import { TableCardContent } from '../../components/common/styled/TableCardContent';

interface BreadcrumbItem {
  label: string;
  isActive?: boolean;
  onClick?: () => void;
}

const LubricationChartsContainer = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const canAddorEdit = canEditLubricationCharts(userInfo?.authorityName);
  const navigate = useNavigate();
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [resetFiltersSignal, setResetFiltersSignal] = useState(false);
  const [tableColumns, setTableColumns] = useState<ColDef[]>([]);

  const { data: lubricationChartsListData = [], isLoading: isLoadingLubricationChartsList } =
    useGetLubricationChartsListDataQuery(undefined, {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    });

  // Breadcrumb options
  const breadcrumbOptions: BreadcrumbItem[] = [
    { label: t('lubricationChart.title'), isActive: true, onClick: () => {} },
  ];

  // Table columns
  const columns: ColDef[] = useMemo(
    () => [
      {
        field: 'vessel',
        headerName: t('table.columns.vessel'),
        width: 200,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'manager',
        headerName: t('table.columns.manager'),
        width: 200,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'activeProducts',
        headerName: t('table.columns.activeProducts'),
        width: 200,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgNumberFilter,
      },
      {
        field: 'typeInProduct',
        headerName: t('table.columns.typeInProducts'),
        width: 200,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgNumberFilter,
      },
      {
        field: 'inactiveProducts',
        headerName: t('table.columns.inactiveProducts'),
        width: 220,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgNumberFilter,
      },
      {
        field: 'lastUpdatedBy',
        headerName: t('table.columns.lastUpdatedBy'),
        width: 180,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'lastUpdatedDate',
        headerName: t('table.columns.lastUpdatedDate'),
        width: 200,
        filter: 'agDateColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgDateFilter,
        filterParams: {
          suppressAndOrCondition: true,
          maxNumConditions: 1,
          filterOptions: ['equals'],
          defaultOption: 'equals',
        },
      },
    ],
    [t],
  );

  useEffect(() => {
    setTableColumns(canAddorEdit ? columns : columns.filter((col) => col.field !== 'actions'));
  }, [canAddorEdit, columns]);

  // cell clicked - safer to detect column
  const onCellClicked = useCallback(
    (params: any) => {
      const colId = params?.column?.getColId?.() ?? params?.colDef?.field;
      if (colId === 'actions') return;
      if (isAdmin(userInfo?.authorityName) || isEditor(userInfo?.authorityName)) {
        return navigate('/lubrication-edit', {
          state: { data: { mode: 'edit', ...params.data } },
        });
      }
      navigate('/lubrication-view', {
        state: { data: { mode: 'view', ...params.data } },
      });
    },
    [navigate, userInfo?.authorityName],
  );

  return (
    <Stack gap={1} sx={{ height: '100%', minHeight: 0 }}>
      <PageHeader
        options={breadcrumbOptions}
        isFilterActive={isFilterActive}
        resetFilters={() => setResetFiltersSignal(true)}
      />
      <TableCardContent>
        <ClientSideAgGrid
          rowData={lubricationChartsListData ?? []}
          columnDefs={tableColumns as any}
          loading={isLoadingLubricationChartsList}
          onCellClicked={onCellClicked}
          className="lubrication-charts-grid"
          onFiltersActiveChange={(active: boolean) => {
            setIsFilterActive(active);
            setResetFiltersSignal(false);
          }}
          resetFiltersSignal={resetFiltersSignal}
        />
      </TableCardContent>
    </Stack>
  );
};

export default LubricationChartsContainer;
